import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Adds bundle component variants to the cart (never the listing product).
 *
 * @typedef {object} BundleAddToCartRefs
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} buttonLabel
 * @property {HTMLElement} addedLabel
 * @property {HTMLElement} errorMessage
 *
 * @extends Component<BundleAddToCartRefs>
 */
class BundleAddToCart extends Component {
  requiredRefs = ['submitButton', 'buttonLabel', 'addedLabel', 'errorMessage'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @param {boolean} disabled */
  #setBusy(disabled) {
    this.querySelectorAll('button').forEach((button) => {
      button.disabled = disabled;
    });
  }

  addBundle = async () => {
    const { buttonLabel, addedLabel, errorMessage } = this.refs;
    let items = [];

    try {
      items = JSON.parse(this.dataset.items || '[]');
    } catch (error) {
      return;
    }

    if (!Array.isArray(items) || items.length < 2) return;

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.#setBusy(true);
    errorMessage.classList.add('hidden');
    errorMessage.textContent = '';

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        signal: abortController.signal,
      });
      const data = await response.json();

      if (data.status) {
        const rawMessage = data.description || data.message || Theme.translations.add_to_cart_error;
        const message = typeof rawMessage === 'string' ? rawMessage : Theme.translations.add_to_cart_error;
        errorMessage.textContent = message;
        errorMessage.classList.remove('hidden');
        this.dispatchEvent(new CartErrorEvent(this.id || 'bundle-add-to-cart', message, message));
        this.#setBusy(false);
        return;
      }

      buttonLabel.classList.add('hidden');
      addedLabel.classList.remove('hidden');

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'bundle-add-to-cart', {
          source: 'bundle-add-to-cart',
          itemCount: items.length,
          sections: data.sections,
        })
      );

      window.setTimeout(() => {
        buttonLabel.classList.remove('hidden');
        addedLabel.classList.add('hidden');
        this.#setBusy(false);
      }, 2000);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      errorMessage.textContent = Theme.translations.add_to_cart_error;
      errorMessage.classList.remove('hidden');
      this.#setBusy(false);
    } finally {
      this.#activeFetch = null;
    }
  };
}

if (!customElements.get('bundle-add-to-cart')) {
  customElements.define('bundle-add-to-cart', BundleAddToCart);
}
